import React, { useState } from 'react';
import { makeStyles } from '@material-ui/core/styles';
import { useMutation } from 'react-apollo';
import Typography from '@material-ui/core/Typography';
import Grid from '@material-ui/core/Grid';
import Box from '@material-ui/core/Box';
import Chip from '@material-ui/core/Chip';
import Card from '@material-ui/core/Card';
import CardContent from '@material-ui/core/CardContent';
import Divider from '@material-ui/core/Divider';
import AddIcon from '@material-ui/icons/Add';
import {
  bindMenu,
  bindTrigger,
  usePopupState,
} from 'material-ui-popup-state/hooks';
import Button from 'components/common/Button';
import Input from 'components/form/Input';
import { UPDATE_DOCUMENT } from 'graphql/student/essays';
import { ESSAY_TYPE } from 'constant/essay';
import useSnack from 'hooks/useSnack';
import Menu from '@material-ui/core/Menu';
import MenuItem from '@material-ui/core/MenuItem';

const useStyles = makeStyles((theme) => ({
  divider: {
    marginBottom: theme.spacing(5),
    marginTop: theme.spacing(5),
  },
  card: {
    marginBottom: theme.spacing(3),
  },
  chip: {
    fontWeight: 700,
    marginRight: 8,
    borderRadius: 6,
  },
  filters: {
    'marginBottom': theme.spacing(2),
    '& > div, button': {
      marginRight: theme.spacing(1),
      marginBottom: theme.spacing(1),
    },
  },
}));

const NoteField = ({ doc, onSave }) => {
  const classes = useStyles();
  const [value, setValue] = useState(doc.notes || '');

  const onChange = (e) => setValue(e.target.value);

  const onBlur = () => {
    onSave(doc, value);
  };

  return (
    <Input
      multiline
      rows={4}
      placeholder="Add your notes here"
      value={value}
      onChange={onChange}
      onBlur={onBlur}
    />
  );
};

const Brainstorm = ({ universities }) => {
  const classes = useStyles();
  const [updateDoc] = useMutation(UPDATE_DOCUMENT);
  const [filters, setFilters] = useState([]);
  const snack = useSnack();
  const popupState = usePopupState({ variant: 'popover', popupId: 'menu' });

  const onBlur = (doc, value) => {
    if (value === doc.notes) return;
    updateDoc({
      variables: {
        id: doc.id,
        data: {
          notes: value,
        },
      },
    }).then(
      () => snack.success('Notes updated'),
      (e) => snack.error(e.message),
    );
  };

  const onFilterSelect = (item) => () => {
    if (!filters.includes(item)) {
      setFilters([...filters, item]);
    }
    popupState.close();
  };

  const onRemoveFilter = (i) => () => {
    setFilters(filters.filter((e) => i !== e));
  };

  const renderUniversity = (u) => {
    const documents = u.documents.filter((doc) =>
      filters.length ? filters.find((f) => f.value === doc.type) : true,
    );

    if (!documents.length) return null;

    return (
      <Card key={u.id} className={classes.card}>
        <CardContent>
          <Typography variant="subtitle1">{u.university_name}</Typography>
        </CardContent>
        <Box marginBottom={5}>
        <Divider />
        </Box>
        <CardContent>
          {documents.map((doc, idx) => (
            <div key={doc.id}>
              <Grid container spacing={3} key={doc.id}>
                <Grid item xs={12} md={6}>
                  <Box marginBottom={2}>
                  <Typography
                    gutterBottom
                    color="textSecondary"
                  >
                    {doc.prompt}
                  </Typography>
                  </Box>
                  <Chip
                    className={classes.chip}
                    label={ESSAY_TYPE.find((e) => e.value === doc.type)?.label}
                  />
                  {doc.word_count && (
                    <Chip
                      className={classes.chip}
                      label={`${doc.word_count} words`}
                    />
                  )}
                </Grid>
                <Grid item xs={12} md={6}>
                  <NoteField doc={doc} onSave={onBlur} />
                </Grid>
              </Grid>
              { idx < documents.length - 1 ? <Divider className={classes.divider} /> : null}
            </div>
          ))}
        </CardContent>
      </Card>
    );
  };

  return (
    <div>
      <Box className={classes.filters}>
        {filters.map((i) => (
          <Chip
            key={i.value}
            onDelete={onRemoveFilter(i)}
            color="primary"
            variant="default"
            label={i.label}
          />
        ))}
        <Button
          color="primary"
          startIcon={<AddIcon />}
          {...bindTrigger(popupState)}
        >
          Add Filter
        </Button>
      </Box>
      {universities.map(renderUniversity)}
      <Menu {...bindMenu(popupState)}>
        {ESSAY_TYPE.map((i) => (
          <MenuItem key={i.value} onClick={onFilterSelect(i)}>
            {i.label}
          </MenuItem>
        ))}
      </Menu>
    </div>
  );
};

export default Brainstorm;
